import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  HStack,
  IconButton,
  Input,
  Spinner,
  VStack,
} from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import { supabaseMutationFn, useSupabaseTypedClient } from 'hooks/reactQuery';
import { useToastWrapper } from 'hooks/useToastWrapper';
import { useCallback, useEffect, useState } from 'react';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import Button from 'components/Button';
import Icon from 'components/Icon';
import Text from 'components/Text';
import {
  ApiKeyInfo,
  EdgeFunctionName,
  GenerateApiKeyResponse,
  ManageApiKeyParams,
} from 'types/edgeFunctions';
import { ERoute } from 'types/frontend';

interface IEndpoint {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  path: string;
  description: string;
}

interface IResource {
  name: string;
  endpoints: IEndpoint[];
}

const RESOURCES: IResource[] = [
  {
    name: 'Users',
    endpoints: [
      { method: 'GET', path: '/mock/users', description: 'List users (paginated). Filter: ?role=admin, ?age_gte=18' },
      { method: 'GET', path: '/mock/users/:id', description: 'Get a single user by ID' },
      { method: 'POST', path: '/mock/users', description: 'Create a user (mock, not persisted)' },
      { method: 'PUT', path: '/mock/users/:id', description: 'Update a user (mock, not persisted)' },
      { method: 'DELETE', path: '/mock/users/:id', description: 'Delete a user (mock, not persisted)' },
    ],
  },
  {
    name: 'Products',
    endpoints: [
      { method: 'GET', path: '/mock/products', description: 'List products (paginated). Filter: ?category=, ?price_gte=, ?price_lte=' },
      { method: 'GET', path: '/mock/products/:id', description: 'Get a single product by ID' },
      { method: 'POST', path: '/mock/products', description: 'Create a product (mock, not persisted)' },
      { method: 'PUT', path: '/mock/products/:id', description: 'Update a product (mock, not persisted)' },
      { method: 'DELETE', path: '/mock/products/:id', description: 'Delete a product (mock, not persisted)' },
    ],
  },
  {
    name: 'Transactions',
    endpoints: [
      { method: 'GET', path: '/mock/transactions', description: 'List transactions (paginated)' },
      { method: 'GET', path: '/mock/transactions/:id', description: 'Get a single transaction' },
      { method: 'POST', path: '/mock/transactions', description: 'Create a transaction (mock, not persisted)' },
    ],
  },
  {
    name: 'Posts',
    endpoints: [
      { method: 'GET', path: '/mock/posts', description: 'List blog posts (paginated). Search: ?q=javascript' },
      { method: 'GET', path: '/mock/posts/:id', description: 'Get a single post' },
      { method: 'POST', path: '/mock/posts', description: 'Create a post (mock, not persisted)' },
      { method: 'PUT', path: '/mock/posts/:id', description: 'Update a post (mock, not persisted)' },
      { method: 'DELETE', path: '/mock/posts/:id', description: 'Delete a post (mock, not persisted)' },
    ],
  },
  {
    name: 'Comments',
    endpoints: [
      { method: 'GET', path: '/mock/comments', description: 'List comments (paginated). Filter: ?post_id=5' },
      { method: 'POST', path: '/mock/comments', description: 'Create a comment (mock, not persisted)' },
    ],
  },
];

const METHOD_COLORS: Record<string, string> = {
  GET: 'green',
  POST: 'blue',
  PUT: 'orange',
  DELETE: 'red',
};

const EDITOR_SNIPPET = `// In the BDS editor (auto-authenticated):
async function fetchUsers() {
  const res = await bds.api('/mock/users?page=1&limit=10');
  const { data, pagination } = await res.json();
  console.log(data, pagination);
}

fetchUsers();`;

const EXTERNAL_SNIPPET = `// From any external app:
async function fetchUsers() {
  const res = await fetch(
    'https://bigdevsoon.me/api/mock/users?page=1&limit=10',
    { headers: { 'x-bds-key': 'YOUR_KEY' } }
  );
  const { data, pagination } = await res.json();
  console.log(data, pagination);
}

fetchUsers();`;

const BigDevToolsTab = () => {
  const supabase = useSupabaseTypedClient();
  const { toastSuccess, toastError } = useToastWrapper();
  const { isFree, isTerminated } = useCurrentUser();

  const [keys, setKeys] = useState<ApiKeyInfo[]>([]);
  const [newRawKey, setNewRawKey] = useState<string | null>(null);
  const [isKeyCopied, setIsKeyCopied] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const apiKeyMutation = useMutation<
    GenerateApiKeyResponse | ApiKeyInfo[] | { success: boolean },
    string,
    ManageApiKeyParams
  >({
    mutationFn: supabaseMutationFn((params) =>
      supabase.functions.invoke(EdgeFunctionName.ManageApiKey, {
        body: params,
      })
    ),
  });

  const isPremium = !isFree && !isTerminated;
  const activeKey = keys.find((k) => k.isActive);

  const loadKeys = useCallback(() => {
    apiKeyMutation.mutate(
      { type: 'list' },
      {
        onSuccess: (data) => {
          setKeys(data as ApiKeyInfo[]);
          setIsLoaded(true);
        },
        onError: () => toastError('Failed to load API keys'),
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isPremium) {
      loadKeys();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGenerate = () => {
    apiKeyMutation.mutate(
      { type: 'generate' },
      {
        onSuccess: (data) => {
          const result = data as GenerateApiKeyResponse;
          setNewRawKey(result.key);
          setIsKeyCopied(false);
          loadKeys();
          toastSuccess('API key generated');
        },
        onError: (error) => toastError(String(error)),
      }
    );
  };

  const handleCopyKey = async () => {
    if (!newRawKey) return;
    try {
      await navigator.clipboard.writeText(newRawKey);
      setIsKeyCopied(true);
      toastSuccess('Copied to clipboard');
    } catch {
      toastError('Failed to copy');
    }
  };

  const handleCopySnippet = async (path: string) => {
    if (!isPremium) return;
    const snippet = `async function fetchData() {
  const res = await bds.api('${path}?page=1&limit=10');
  const { data, pagination } = await res.json();
  console.log(data, pagination);
}

fetchData();`;
    try {
      await navigator.clipboard.writeText(snippet);
      toastSuccess('Snippet copied');
    } catch {
      toastError('Failed to copy');
    }
  };

  const handleCopyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      toastSuccess('Copied to clipboard');
    } catch {
      toastError('Failed to copy');
    }
  };

  const settingsLink = `${ERoute.Settings}#api-keys`;

  const renderApiKeySection = () => {
    if (!isPremium) {
      return (
        <Box p={3} bg="whiteAlpha.100" borderRadius="md" position="relative">
          <Text fontSize="small" fontWeight="semibold" mb={2}>
            Your API Key
          </Text>
          <HStack spacing={2} mb={3}>
            <Input
              value="bds_••••••••••••••••••••"
              isReadOnly
              size="sm"
              bg="blackAlpha.400"
              fontFamily="mono"
              fontSize="xs"
              isDisabled
            />
            <Badge colorScheme="gray" fontSize="xs">
              Locked
            </Badge>
          </HStack>
          <Button scheme="gradient" size="sm" to={ERoute.Plans} leftIcon={<Icon type="Rocket" />} width="100%">
            Upgrade to BIG to generate your API key
          </Button>
        </Box>
      );
    }

    return (
      <Box p={3} bg="whiteAlpha.100" borderRadius="md">
        <HStack justify="space-between" mb={2}>
          <Text fontSize="small" fontWeight="semibold">
            Your API Key
          </Text>
          <Button scheme="ghost" size="xs" to={settingsLink} rightIcon={<Icon type="Settings" size={12} />}>
            Manage
          </Button>
        </HStack>
        {!isLoaded ? (
          <Spinner size="sm" />
        ) : newRawKey ? (
          <VStack align="stretch" spacing={2}>
            <Text fontSize="micro" color="yellow.200">
              Copy now — will not be shown again
            </Text>
            <HStack spacing={2}>
              <Input
                value={newRawKey}
                isReadOnly
                size="sm"
                bg="blackAlpha.400"
                fontFamily="mono"
                fontSize="xs"
              />
              <Button
                scheme={isKeyCopied ? 'ghost' : 'primary'}
                size="xs"
                onClick={handleCopyKey}
              >
                {isKeyCopied ? 'Copied' : 'Copy'}
              </Button>
            </HStack>
          </VStack>
        ) : activeKey ? (
          <HStack spacing={2}>
            <Text fontSize="small" fontFamily="mono" opacity="secondary">
              {activeKey.keyPrefix}...
            </Text>
            <Badge colorScheme="green" fontSize="xs">
              Active
            </Badge>
            <Text fontSize="micro" opacity="tertiary">
              {activeKey.requestsThisMonth} request{activeKey.requestsThisMonth === 1 ? '' : 's'} this month
            </Text>
          </HStack>
        ) : (
          <VStack align="start" spacing={2}>
            <Text fontSize="small" opacity="secondary">
              No API key yet.
            </Text>
            <Button
              scheme="primary"
              size="xs"
              onClick={handleGenerate}
              isLoading={apiKeyMutation.isPending}
            >
              Generate API Key
            </Button>
          </VStack>
        )}
      </Box>
    );
  };

  return (
    <VStack align="stretch" spacing={5}>
      <Box>
        <Text fontSize="large" fontWeight="semibold" mb={1}>
          BigDevTools API
        </Text>
        <Text fontSize="small" opacity="secondary">
          Mock API endpoints for building real-world apps. Use{' '}
          <Box as="code" fontSize="xs" bg="whiteAlpha.200" px={1} borderRadius="sm">
            bds.api()
          </Box>{' '}
          in the editor or call externally with your API key.
        </Text>
      </Box>

      {/* API Key Section — inline upgrade CTA for free users */}
      {renderApiKeySection()}

      {/* Quick Start */}
      <Box>
        <Text fontSize="small" fontWeight="semibold" mb={2}>
          Quick Start
        </Text>
        <Box position="relative">
          <Box
            p={3}
            bg="blackAlpha.400"
            borderRadius="md"
            fontFamily="mono"
            fontSize="xs"
            whiteSpace="pre"
            overflowX="auto"
          >
            {EDITOR_SNIPPET}
          </Box>
          <IconButton
            aria-label="Copy snippet"
            icon={<Icon type="Copy" size={14} />}
            size="xs"
            variant="ghost"
            position="absolute"
            top={1}
            right={1}
            onClick={() => handleCopyCode(EDITOR_SNIPPET)}
          />
        </Box>
        <Box position="relative" mt={2}>
          <Box
            p={3}
            bg="blackAlpha.400"
            borderRadius="md"
            fontFamily="mono"
            fontSize="xs"
            whiteSpace="pre"
            overflowX="auto"
          >
            {EXTERNAL_SNIPPET}
          </Box>
          <IconButton
            aria-label="Copy snippet"
            icon={<Icon type="Copy" size={14} />}
            size="xs"
            variant="ghost"
            position="absolute"
            top={1}
            right={1}
            onClick={() => handleCopyCode(EXTERNAL_SNIPPET)}
          />
        </Box>
      </Box>

      {/* Endpoint Browser */}
      <Box>
        <Text fontSize="small" fontWeight="semibold" mb={2}>
          Endpoints
        </Text>
        <Text fontSize="micro" opacity="tertiary" mb={3}>
          All GET lists support: ?page=1&limit=10 (max 100), ?sort=name&order=asc, ?q=search, ?seed=42
        </Text>
        <Accordion allowMultiple>
          {RESOURCES.map((resource) => (
            <AccordionItem key={resource.name} border="none" mb={1}>
              <AccordionButton
                bg="whiteAlpha.100"
                borderRadius="md"
                _hover={{ bg: 'whiteAlpha.200' }}
                _expanded={{ borderBottomRadius: 0 }}
                px={3}
                py={2}
              >
                <Text fontSize="small" fontWeight="semibold" flex="1" textAlign="left">
                  {resource.name}
                </Text>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel
                pb={2}
                px={0}
                bg="whiteAlpha.50"
                borderBottomRadius="md"
              >
                <VStack align="stretch" spacing={0}>
                  {resource.endpoints.map((ep) => (
                    <HStack
                      key={`${ep.method}-${ep.path}`}
                      px={3}
                      py={2}
                      _hover={{ bg: isPremium ? 'whiteAlpha.100' : undefined }}
                      cursor={isPremium ? 'pointer' : 'default'}
                      onClick={() => handleCopySnippet(ep.path)}
                      spacing={3}
                      opacity={isPremium ? 1 : 0.6}
                    >
                      <Badge
                        colorScheme={METHOD_COLORS[ep.method]}
                        fontSize="10px"
                        minW="50px"
                        textAlign="center"
                      >
                        {ep.method}
                      </Badge>
                      <Text fontSize="micro" fontFamily="mono" flex="1">
                        {ep.path}
                      </Text>
                      {isPremium && (
                        <Icon type="Copy" size={12} />
                      )}
                    </HStack>
                  ))}
                </VStack>
              </AccordionPanel>
            </AccordionItem>
          ))}
        </Accordion>
      </Box>

      {/* Rate Limits */}
      <Box p={3} bg="whiteAlpha.50" borderRadius="md">
        <Text fontSize="micro" opacity="tertiary">
          Rate limits: Trial 10 req/min, Premium 100 req/min. POST/PUT/DELETE return mock
          responses (data is not persisted).
        </Text>
      </Box>
    </VStack>
  );
};

export default BigDevToolsTab;
